// src/contexts/NotificationContext.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense — app-wide notifications, now powered by FlowSense Notify.
//
// --- UPDATE (toast once) --------------------------------------------------------
// Why: notifications were popping up again and again. The old context raised its own toast for
// every websocket 'new_notification' event, while FlowSense Notify (live stream), the old Firebase
// web worker and the browser push worker could all show the same message — and nothing remembered
// what had already been shown across reloads, reconnects or tabs.
//
// Now there is exactly ONE place that shows notifications: <NotifyProvider> (FlowSense Notify).
// It shows each one once (per id across tabs and reloads, and identical repeats within 30 minutes
// are not shown again), only in the visible tab, and closes the toast when you read it anywhere.
// This context raises NO toasts.
//
// The hook keeps the exact API the app already uses (40+ components):
//   const { notifications, unreadCount, markAsRead, markAllAsRead, refresh, isConnected } = useNotifications();
//   • isConnected — the FlowSense live websocket (sensors, work orders …) OR the Notify stream
//   • notifications — latest 20 inbox items in the legacy shape ({ id, title, message, body, type,
//     priority, read, created_at, … }), kept live
// Plus: summary, status, openCenter() (the full notification centre).
// --- END UPDATE -----------------------------------------------------------------
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NotifyProvider, useNotify } from '../components/notify/NotifyProvider';
import notifyStream from '../services/notifyStream';
import * as notifyApi from '../services/notifyApi';
import webSocketService from '../services/websocketService';
import { getAuthToken } from '../utils/auth';

const NotificationContext = createContext(null);

const FALLBACK = Object.freeze({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  status: 'idle',
  summary: null,
  markAsRead: () => Promise.resolve(),
  markAllAsRead: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
  openCenter: () => {},
});

const LIST_SIZE = 20;

/** FlowSense Notify item → the shape the existing components read. */
export const toLegacy = (n) => ({
  ...n,
  message: n.body,
  read: Boolean(n.is_read),
  is_read: Boolean(n.is_read),
  created_at: n.created_at || new Date().toISOString(),
});

const wsConnectedNow = () => {
  try {
    const v = webSocketService?.isConnected;
    return typeof v === 'function' ? Boolean(v.call(webSocketService)) : Boolean(v);
  } catch (_) { return false; }
};

function LegacyBridge({ enabled, children }) {
  const { summary, status, refreshSummary, openCenter } = useNotify();
  const [items, setItems] = useState([]);
  const [wsConnected, setWsConnected] = useState(wsConnectedNow);
  const isMounted = useRef(true);
  const ctrl = useRef(null);

  useEffect(() => () => { isMounted.current = false; ctrl.current?.abort?.(); }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    ctrl.current?.abort?.();
    const ac = typeof AbortController === 'function' ? new AbortController() : null;
    ctrl.current = ac;
    try {
      const { items: list } = await notifyApi.getInbox({ limit: LIST_SIZE }, ac?.signal);
      if (isMounted.current && ctrl.current === ac) setItems(list.map(toLegacy));
    } catch (_) { /* keep the last list — the live stream keeps the badge right */ }
  }, [enabled]);

  // first page + live updates (no toasts here — NotifyProvider owns those)
  useEffect(() => {
    if (!enabled) { setItems([]); return undefined; }
    load();
    const offs = [
      notifyStream.on('notification', (p) => {
        const n = p?.notification;
        if (!n?.id || !isMounted.current) return;
        setItems(prev => (prev.some(x => x.id === n.id) ? prev : [toLegacy(n), ...prev].slice(0, LIST_SIZE)));
      }),
      notifyStream.on('notification_update', (c) => {
        if (!c || !isMounted.current) return;
        const ids = new Set([].concat(c.ids || []).map(String));
        setItems(prev => {
          switch (c.action) {
            case 'read_all': return prev.map(x => (c.category && x.category !== c.category ? x : { ...x, read: true, is_read: true }));
            case 'read': case 'acknowledged': return prev.map(x => (ids.has(String(x.id)) ? { ...x, read: true, is_read: true, ...(c.action === 'acknowledged' ? { acknowledged_at: new Date().toISOString() } : {}) } : x));
            case 'unread': return prev.map(x => (ids.has(String(x.id)) ? { ...x, read: false, is_read: false } : x));
            case 'archived': case 'deleted': case 'snoozed': return prev.filter(x => !ids.has(String(x.id)));
            default: return prev;
          }
        });
      }),
      notifyStream.on('hello', () => load()), // reconnected → catch up
    ];
    return () => offs.forEach(off => off());
  }, [enabled, load]);

  // websocket connection state (pages show "Live" from this). Opens the shared socket once when
  // signed in, as the previous context did (the service ignores a second connect).
  useEffect(() => {
    if (!enabled) return;
    try {
      const flag = (v) => (typeof v === 'function' ? v.call(webSocketService) : v);
      if (!flag(webSocketService?.isConnected) && !flag(webSocketService?.isConnecting)) webSocketService?.connect?.();
    } catch (_) { /* pages connect on their own as well */ }
  }, [enabled]);

  useEffect(() => {
    if (!webSocketService?.on) return undefined;
    const onUp = () => { if (isMounted.current) setWsConnected(true); };
    const onDown = () => { if (isMounted.current) setWsConnected(false); };
    webSocketService.on('connect', onUp);
    webSocketService.on('disconnect', onDown);
    setWsConnected(wsConnectedNow());
    return () => {
      try { webSocketService.off?.('connect', onUp); webSocketService.off?.('disconnect', onDown); } catch (_) { /* noop */ }
    };
  }, []);

  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    setItems(prev => prev.map(x => (x.id === id ? { ...x, read: true, is_read: true } : x)));
    try { await notifyApi.markRead([id]); } catch (_) { load(); }
  }, [load]);

  const markAllAsRead = useCallback(async () => {
    setItems(prev => prev.map(x => ({ ...x, read: true, is_read: true })));
    try { await notifyApi.readAll(); } catch (_) { load(); }
    refreshSummary();
  }, [load, refreshSummary]);

  const refresh = useCallback(async () => {
    await Promise.all([load(), refreshSummary()]);
  }, [load, refreshSummary]);

  const notifyLive = status === 'live' || status === 'polling';
  const unreadCount = summary?.unread ?? items.filter(x => !x.read).length;

  const value = useMemo(() => ({
    notifications: items,
    unreadCount,
    isConnected: wsConnected || notifyLive,
    status,
    summary,
    markAsRead,
    markAllAsRead,
    refresh,
    openCenter,
  }), [items, unreadCount, wsConnected, notifyLive, status, summary, markAsRead, markAllAsRead, refresh, openCenter]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

/**
 * Mount once, inside <BrowserRouter> (App.js already does). Notifications run only on signed-in
 * pages (/admin/*); the auth pages stay quiet.
 */
export function NotificationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onAdmin = location.pathname.startsWith('/admin');
  const enabled = onAdmin && Boolean(getAuthToken());

  return (
    <NotifyProvider navigate={navigate} enabled={enabled}>
      <LegacyBridge enabled={enabled}>{children}</LegacyBridge>
    </NotifyProvider>
  );
}

export const useNotifications = () => useContext(NotificationContext) || FALLBACK;

export { NotificationContext };
export default NotificationContext;
